import { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Minus, Plus, ShoppingBag, Trash2 } from 'lucide-react';

import { Sheet, SheetContent, SheetHeader, SheetTitle } from '@/components/ui/sheet';
import { Button } from '@/components/ui/button';
import { Progress } from '@/components/ui/progress';
import { cn, formatPrice } from '@/lib/utils';
import { useCartDrawerStore } from '@/store/cartDrawerStore';
import { useShopCartStore } from '@/store/shopCartStore';
import { useShopCheckoutPrefsStore } from '@/store/shopCheckoutPrefsStore';
import { MIN_DELIVERY_ORDER_AMOUNT_ILS, getDeliveryMinimumMessage } from '@/lib/deliveryMinimum';
import { useAuthContext } from '@/contexts/AuthContext';
import { useShopProducts, type ShopProduct } from '@/hooks/useShopProducts';
import { getProductImageUrl } from '@/lib/shopUtils';
import { CATEGORY_LABELS } from '@/config/catalog';
import { supabase } from '@/integrations/supabase/client';
import { CouponInput } from '@/components/CouponInput';

type CustomBouquetItem = NonNullable<ShopProduct['customBouquetMetadata']>['items'][number];

function getCategoryLabel(category: string): string {
  const fromCatalog = (CATEGORY_LABELS as Record<string, string>)[category];
  if (fromCatalog) return fromCatalog;
  const fallback: Record<string, string> = {
    flowers: 'פרחים',
    greens: 'ירק',
    filler: 'ממלאי נפח',
    fillers: 'ממלאי נפח',
    wrap: 'עטיפה',
    wraps: 'עטיפה',
    addons: 'תוספות',
    'add-ons': 'תוספות',
    card: 'כרטיס ברכה',
  };
  return fallback[category] || category;
}

function groupItemsByCategory(items: CustomBouquetItem[]): Array<[string, CustomBouquetItem[]]> {
  const grouped = items.reduce<Record<string, CustomBouquetItem[]>>((acc, it) => {
    const key = it.category || 'other';
    if (!acc[key]) acc[key] = [];
    acc[key].push(it);
    return acc;
  }, {});

  const categoryOrder = ['wrap', 'wraps', 'flowers', 'greens', 'filler', 'fillers', 'addons', 'add-ons', 'card', 'other'];
  return Object.entries(grouped).sort(([a], [b]) => {
    const ia = categoryOrder.indexOf(a);
    const ib = categoryOrder.indexOf(b);
    if (ia === -1 && ib === -1) return a.localeCompare(b);
    if (ia === -1) return 1;
    if (ib === -1) return -1;
    return ia - ib;
  });
}

function getCartItemImageSrc(product: ShopProduct): string {
  // Premium branding: show Florisimo logo for custom bouquets (matches checkout behavior).
  if (product.customBouquetMetadata) return '/florissimo-logo.png';
  return getProductImageUrl(product);
}

function curateUpsell(products: ShopProduct[]): ShopProduct[] {
  const byKeywords = (keywords: string[]) =>
    products.find((p) => keywords.some((k) => p.nameHe.includes(k)));

  const picks = [
    byKeywords(['שוקולד', 'פרלינים']),
    byKeywords(['כרטיס', 'ברכה']),
    byKeywords(['אגרטל', 'וואזה', 'בלון', 'יין']),
  ].filter(Boolean) as ShopProduct[];

  const unique = Array.from(new Map(picks.map((p) => [p.id, p])).values());
  return unique.slice(0, 3);
}

export function CartDrawer() {
  const navigate = useNavigate();
  const isOpen = useCartDrawerStore((s) => s.isOpen);
  const setOpen = useCartDrawerStore((s) => s.setOpen);

  const { user, isMember } = useAuthContext();
  const { data: allProducts = [] } = useShopProducts();

  const items = useShopCartStore((s) => s.items);
  const updateQuantity = useShopCartStore((s) => s.updateQuantity);
  const removeItem = useShopCartStore((s) => s.removeItem);
  const addItem = useShopCartStore((s) => s.addItem);
  const totalItems = useShopCartStore((s) => s.getTotalItems());
  const getTotalPrice = useShopCartStore((s) => s.getTotalPrice);
  const getMemberTotalPrice = useShopCartStore((s) => s.getMemberTotalPrice);
  const couponCode = useShopCartStore((s) => s.couponCode);
  const couponDiscount = useShopCartStore((s) => s.couponDiscount);
  const couponDiscountPercent = useShopCartStore((s) => s.couponDiscountPercent);
  const setCoupon = useShopCartStore((s) => s.setCoupon);
  const clearCoupon = useShopCartStore((s) => s.clearCoupon);

  const preferredDeliveryMethod = useShopCheckoutPrefsStore((s) => s.preferredDeliveryMethod);
  const setPreferredDeliveryMethod = useShopCheckoutPrefsStore((s) => s.setPreferredDeliveryMethod);

  const isLoggedInMember = Boolean(user && isMember);
  const rawSubtotal = isLoggedInMember ? getMemberTotalPrice() : getTotalPrice();
  const subtotalAfterDiscount = couponDiscount > 0 ? Math.max(0, rawSubtotal - couponDiscount) : rawSubtotal;
  const isDelivery = preferredDeliveryMethod === 'delivery';
  const isCheckoutBlockedByMinimum = isDelivery && subtotalAfterDiscount < MIN_DELIVERY_ORDER_AMOUNT_ILS;
  const remainingToMinimum = Math.max(0, MIN_DELIVERY_ORDER_AMOUNT_ILS - subtotalAfterDiscount);
  const deliveryProgressPct = Math.max(0, Math.min(1, subtotalAfterDiscount / MIN_DELIVERY_ORDER_AMOUNT_ILS));

  const upsellProducts = useMemo(() => {
    if (!isCheckoutBlockedByMinimum) return [];
    const cartProductIds = new Set(items.map((i) => i.product.id));
    const candidates = allProducts
      .filter((p) => p.category === 'add-ons')
      .filter((p) => p.inStock)
      .filter((p) => !cartProductIds.has(p.id));
    return curateUpsell(candidates);
  }, [allProducts, isCheckoutBlockedByMinimum, items]);

  const handleCheckout = () => {
    if (isCheckoutBlockedByMinimum) return;
    setOpen(false);
    navigate('/shop/checkout');
  };

  return (
    <Sheet open={isOpen} onOpenChange={setOpen}>
      <SheetContent
        side="right"
        className="w-full sm:max-w-md overflow-y-auto bg-background/95 backdrop-blur-xl"
      >
        <SheetHeader>
          <SheetTitle className="font-serif text-xl tracking-wide">הסל שלך</SheetTitle>
          <p className="text-sm text-muted-foreground">{totalItems} פריטים</p>
        </SheetHeader>

        {/* Delivery/Pickup toggle */}
        <div className="mt-6 rounded-2xl border border-border/50 bg-card p-3 shadow-soft">
          <div className="grid grid-cols-2 gap-2">
            <Button
              type="button"
              variant={preferredDeliveryMethod === 'pickup' ? 'default' : 'outline'}
              className="w-full"
              onClick={() => setPreferredDeliveryMethod('pickup')}
            >
              איסוף עצמי
            </Button>
            <Button
              type="button"
              variant={preferredDeliveryMethod === 'delivery' ? 'default' : 'outline'}
              className="w-full"
              onClick={() => setPreferredDeliveryMethod('delivery')}
            >
              משלוח
            </Button>
          </div>
        </div>

        {/* Items */}
        <div className="mt-6 space-y-3">
          {items.length === 0 ? (
            <div className="rounded-2xl border border-border/50 bg-card p-6 text-center shadow-soft">
              <p className="font-serif text-lg">הסל ריק</p>
              <p className="mt-2 text-sm text-muted-foreground">הוסיפו פריטים מהחנות כדי להמשיך.</p>
              <Button className="mt-4" variant="outline" onClick={() => setOpen(false)}>
                המשך לקנות
              </Button>
            </div>
          ) : (
            items.map((item) => (
              <div
                key={item.product.id}
                className="rounded-2xl border border-border/50 bg-card p-4 shadow-soft"
              >
                <div className="flex gap-3">
                  <img
                    src={getCartItemImageSrc(item.product)}
                    alt={item.product.nameHe}
                    loading="lazy"
                    className="h-16 w-16 rounded-xl bg-background object-contain p-2"
                    onError={(e) => {
                      const target = e.target as HTMLImageElement;
                      target.src = item.product.customBouquetMetadata ? '/florissimo-logo.png' : '/placeholder.svg';
                    }}
                  />
                  <div className="min-w-0 flex-1">
                    <div className="flex items-start justify-between gap-2">
                      <p className="font-sans text-sm font-medium text-foreground line-clamp-2">
                        {item.product.nameHe}
                      </p>
                      <button
                        type="button"
                        className="text-muted-foreground hover:text-foreground transition-colors"
                        onClick={() => removeItem(item.product.id)}
                        aria-label="הסר פריט"
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>

                    {item.product.customBouquetMetadata?.items?.length ? (
                      <div className="mt-3 rounded-xl border border-border/40 bg-background/60 p-3">
                        <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">מתכון הזר</p>
                        <div className="mt-2 space-y-3">
                          {groupItemsByCategory(item.product.customBouquetMetadata.items).map(([category, catItems]) => (
                            <div key={category} className="space-y-1">
                              <p className="text-[11px] font-semibold text-muted-foreground">{getCategoryLabel(category)}</p>
                              <ul className="space-y-1">
                                {catItems.map((ci, idx) => (
                                  <li key={`${ci.itemId}-${idx}`} className="flex items-center justify-between gap-3">
                                    <span className="text-xs text-foreground/90 line-clamp-1">{ci.name}</span>
                                    <span className="text-xs text-muted-foreground">x{ci.bundleCount}</span>
                                  </li>
                                ))}
                              </ul>
                            </div>
                          ))}
                        </div>
                      </div>
                    ) : null}

                    <div className="mt-3 flex items-center justify-between">
                      <div className="flex items-center gap-2 rounded-xl bg-secondary/40 p-1">
                        <Button
                          type="button"
                          variant="ghost"
                          size="icon"
                          className="h-9 w-9"
                          onClick={() => updateQuantity(item.product.id, item.quantity - 1)}
                        >
                          <Minus className="h-4 w-4" />
                        </Button>
                        <span className="w-7 text-center font-serif text-base font-semibold">
                          {item.quantity}
                        </span>
                        <Button
                          type="button"
                          variant="ghost"
                          size="icon"
                          className="h-9 w-9"
                          onClick={() => updateQuantity(item.product.id, item.quantity + 1)}
                        >
                          <Plus className="h-4 w-4" />
                        </Button>
                      </div>

                      <p className="font-serif font-semibold">₪{item.product.price * item.quantity}</p>
                    </div>
                  </div>
                </div>
              </div>
            ))
          )}
        </div>

        {/* Summary + CTA */}
        {items.length > 0 && (
          <div className="mt-6 rounded-2xl border border-border/50 bg-card p-4 shadow-soft space-y-3">
            {/* Coupon Input */}
            <CouponInput
              onApply={async (code) => {
                try {
                  const cartItems = items.map((i) => ({
                    itemId: i.product.id,
                    name: i.product.nameHe,
                    type: i.product.category,
                    tags: [] as string[],
                    quantity: i.quantity,
                    unitPrice: i.product.price,
                  }));
                  const { data, error } = await supabase.functions.invoke('compute-pricing', {
                    body: { items: cartItems, couponCode: code },
                  });
                  if (error || !data) {
                    return { valid: false, discountAmount: 0, discountPercent: 0, errorMessage: 'שגיאה באימות הקופון' };
                  }
                  const coupon = data.coupon;
                  if (coupon && coupon.discountAmount > 0) {
                    setCoupon(coupon.code, coupon.discountAmount, coupon.discountPercent);
                    return { valid: true, discountAmount: coupon.discountAmount, discountPercent: coupon.discountPercent };
                  }
                  return { valid: false, discountAmount: 0, discountPercent: 0, errorMessage: 'קוד קופון לא תקין' };
                } catch {
                  return { valid: false, discountAmount: 0, discountPercent: 0, errorMessage: 'שגיאה באימות הקופון' };
                }
              }}
              onRemove={clearCoupon}
              appliedCode={couponCode}
              appliedDiscount={couponDiscount}
              appliedPercent={couponDiscountPercent}
            />

            <div className="flex items-center justify-between">
              <p className="text-sm text-muted-foreground">סה״כ ביניים</p>
              <p className="font-serif text-lg font-semibold">{formatPrice(subtotalAfterDiscount)}</p>
            </div>

            {isDelivery && (
              <div className="mt-3 rounded-xl bg-secondary/30 p-3">
                <div className="flex items-center justify-between gap-3">
                  <p className="text-sm text-foreground">{getDeliveryMinimumMessage()}</p>
                  <p className="text-xs text-muted-foreground">
                    {remainingToMinimum > 0 ? `חסרים ₪${remainingToMinimum}` : 'הגעת למינימום'}
                  </p>
                </div>
                <Progress value={Math.round(deliveryProgressPct * 100)} className="mt-2 h-2" />
              </div>
            )}

            {isCheckoutBlockedByMinimum && (
              <div className="mt-3 rounded-xl bg-secondary/40 p-3">
                <p className="mt-1 text-xs text-muted-foreground">נשאר עוד ₪{remainingToMinimum} כדי להגיע למינימום.</p>
              </div>
            )}

            <Button
              type="button"
              className={cn('mt-4 w-full gap-2', isCheckoutBlockedByMinimum && 'opacity-90')}
              onClick={handleCheckout}
              disabled={isCheckoutBlockedByMinimum}
            >
              <ShoppingBag className="h-4 w-4" />
              לקופה
            </Button>
          </div>
        )}

        {/* Upsell */}
        {isCheckoutBlockedByMinimum && upsellProducts.length > 0 && (
          <div className="mt-6 rounded-2xl border border-border/50 bg-card p-4 shadow-soft">
            <p className="font-serif text-base tracking-wide">חסר לך קצת למינימום? הוסף מוצר משלים</p>
            <div className="mt-3 space-y-2">
              {upsellProducts.map((p) => (
                <div key={p.id} className="flex items-center justify-between gap-3 rounded-xl bg-background/60 p-2">
                  <div className="flex items-center gap-3 min-w-0">
                    <img
                      src={getProductImageUrl(p)}
                      alt={p.nameHe}
                      loading="lazy"
                      className="h-12 w-12 rounded-lg bg-background object-contain p-2"
                      onError={(e) => {
                        const target = e.target as HTMLImageElement;
                        target.src = '/placeholder.svg';
                      }}
                    />
                    <div className="min-w-0">
                      <p className="text-sm font-medium text-foreground line-clamp-1">{p.nameHe}</p>
                      <p className="text-xs text-muted-foreground">₪{p.price}</p>
                    </div>
                  </div>
                  <Button type="button" size="sm" variant="outline" onClick={() => addItem(p)}>
                    הוסף
                  </Button>
                </div>
              ))}
            </div>
          </div>
        )}
      </SheetContent>
    </Sheet>
  );
}
